import { DICT_KO_EN } from './i18n-dict';

const STORAGE_KEY = 'lang';
type LangMode = 'ko' | 'en';

const KOREAN_RE = /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/;

/** 번역 제외 대상 CSS 선택자 — 사용자 콘텐츠 영역 */
const SKIP_SELECTORS = '.markdown-body, .ql-editor, [data-i18n-skip], pre, code, .ck-editor__editable, .ck-editor';

export class I18n {
    private _current: LangMode = 'ko';
    private _observer: MutationObserver | null = null;
    private _originals = new WeakMap<Node, string>();
    private _attrOriginals = new WeakMap<Element, Record<string, string>>();
    private _dict: Record<string, string> = {};
    private _reverseDict: Record<string, string> = {};
    private _reverseExactMap: Map<string, string> = new Map();
    private _sortedKeys: string[] = [];
    private _sortedReverseKeys: string[] = [];
    private _reverseRegexes: Map<string, RegExp> = new Map();

    constructor() {
        this._buildDict();
        this._load();
        this._translateAll();
        this._observe();
    }

    get current(): LangMode { return this._current; }
    get isKo(): boolean { return this._current === 'ko'; }
    get isEn(): boolean { return this._current === 'en'; }

    public set(lang: LangMode) {
        if (this._current === lang) return;
        this._current = lang;
        this._save();
        this._translateAll();
    }

    public toggle() {
        this.set(this._current === 'ko' ? 'en' : 'ko');
    }

    public destroy() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    private _buildDict() {
        this._dict = { ...DICT_KO_EN };
        this._reverseDict = {};
        this._reverseExactMap = new Map();
        for (const [ko, en] of Object.entries(this._dict)) {
            this._reverseDict[en] = ko;
            // 정확한 전체 문자열 매칭용 (trim 비교)
            this._reverseExactMap.set(en.trim(), ko);
        }
        this._sortedKeys = Object.keys(this._dict).sort((a, b) => b.length - a.length);
        this._sortedReverseKeys = Object.keys(this._reverseDict).sort((a, b) => b.length - a.length);
        // 역방향 키별 단어경계 정규식 사전 빌드
        this._reverseRegexes = new Map();
        for (const key of this._sortedReverseKeys) {
            const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            this._reverseRegexes.set(key, new RegExp(`(?<![\\w])${escaped}(?![\\w])`, 'g'));
        }
    }

    private _load() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY) as LangMode | null;
            if (stored && ['ko', 'en'].includes(stored)) this._current = stored;
        } catch { /* Keep the default when browser storage is unavailable. */ }
    }

    private _save() {
        try {
            localStorage.setItem(STORAGE_KEY, this._current);
        } catch { /* Language switching still works for the current page. */ }
    }

    /** Language changes need a full pass; ordinary rendering only visits changed nodes. */
    private _translateAll() {
        this._observer?.disconnect();
        try {
            this._translateTree(document.body);
            const html = document.documentElement;
            html.lang = this._current;
            if (html.classList.contains('i18n-pending')) {
                html.classList.remove('i18n-pending');
                html.classList.add('i18n-ready');
            }
        } finally {
            this._observeBody();
        }
    }

    private _observeBody() {
        this._observer?.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['placeholder', 'title', 'aria-label'],
        });
    }

    /** DOM 트리 순회 */
    private _translateTree(root: Node) {
        if (root.nodeType === Node.TEXT_NODE) {
            this._translateTextNode(root as Text);
            return;
        }
        if (root instanceof Element && root.closest(SKIP_SELECTORS)) return;
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        let node: Text | null;
        while ((node = walker.nextNode() as Text | null)) {
            this._translateTextNode(node);
        }
        if (root instanceof Element) {
            // Newly inserted inputs/buttons may carry attributes on the root itself.
            this._translateAttributes(root);
            root.querySelectorAll('[placeholder],[title],[aria-label]').forEach(el => this._translateAttributes(el));
        }
    }

    /** 텍스트 노드 번역 */
    private _translateTextNode(node: Text) {
        const parent = node.parentElement;
        if (!parent) return;
        const tag = parent.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return;

        // 콘텐츠 영역 (매뉴얼, 에디터 등) 내부 노드 스킵
        if (parent.closest(SKIP_SELECTORS)) return;

        const text = node.textContent || '';
        if (!text.trim()) return;

        let original: string;

        if (this._originals.has(node)) {
            original = this._originals.get(node)!;

            // Angular가 텍스트를 변경했는지 확인 (예: 탭 전환, 데이터 바인딩)
            const translatedText = this._replaceAll(original, this._dict, this._sortedKeys);

            // A language switch must restore the stored original, not reverse-translate
            // the previous language (multiple Korean phrases can share one translation).
            if (text !== original && text !== translatedText) {
                // 현재 텍스트가 기대값과 다름 → Angular가 새 값을 설정한 것
                original = this._resolveKoreanOriginal(text);
                this._originals.set(node, original);
            }
        } else {
            original = this._resolveKoreanOriginal(text);
            this._originals.set(node, original);
        }

        const newText = this._current === 'en'
            ? this._replaceAll(original, this._dict, this._sortedKeys)
            : original;

        if (node.textContent !== newText) {
            node.textContent = newText;
        }
    }

    /** 텍스트에서 한국어 원본을 복원 */
    private _resolveKoreanOriginal(text: string): string {
        // 한국어 문자가 있으면 이미 한국어 (원본)
        if (KOREAN_RE.test(text)) return text;
        // 정확한 전체 매칭 우선 (trim 비교)
        const trimmed = text.trim();
        const exactMatch = this._reverseExactMap.get(trimmed);
        if (exactMatch) {
            return text.replace(trimmed, exactMatch);
        }
        // 단어 경계로 역방향 매칭 시도
        if (this._containsDictWordBound(text)) {
            return this._replaceAllReverse(text);
        }
        return text;
    }

    /** 속성 번역 (placeholder, title, aria-label) */
    private _translateAttributes(el: Element) {
        // 콘텐츠 영역 내부 스킵
        if (el.closest(SKIP_SELECTORS)) return;

        const attrs = ['placeholder', 'title', 'aria-label'];
        for (const attr of attrs) {
            const val = el.getAttribute(attr);
            if (!val || !val.trim()) continue;

            let rec = this._attrOriginals.get(el);
            if (!rec) { rec = {}; this._attrOriginals.set(el, rec); }

            if (attr in rec) {
                const storedOriginal = rec[attr];
                const translatedVal = this._replaceAll(storedOriginal, this._dict, this._sortedKeys);

                if (val !== storedOriginal && val !== translatedVal) {
                    rec[attr] = this._resolveKoreanOriginal(val);
                }
            } else {
                rec[attr] = this._resolveKoreanOriginal(val);
            }

            const original = rec[attr];
            const newVal = this._current === 'en'
                ? this._replaceAll(original, this._dict, this._sortedKeys)
                : original;

            if (el.getAttribute(attr) !== newVal) {
                el.setAttribute(attr, newVal);
            }
        }
    }

    /** 사전 키 포함 여부 */
    private _containsDict(text: string, dict: Record<string, string>, keys: string[]): boolean {
        for (const key of keys) {
            if (text.includes(key)) return true;
        }
        return false;
    }

    /** 역방향 사전 키 포함 여부 (단어 경계 기반) */
    private _containsDictWordBound(text: string): boolean {
        for (const key of this._sortedReverseKeys) {
            if (!text.includes(key)) continue;
            const regex = this._reverseRegexes.get(key);
            if (regex) {
                regex.lastIndex = 0;
                if (regex.test(text)) return true;
            }
        }
        return false;
    }

    /** 역방향 사전 치환 (단어 경계 기반 — 부분 매칭 방지) */
    private _replaceAllReverse(text: string): string {
        let result = text;
        for (const key of this._sortedReverseKeys) {
            if (!result.includes(key)) continue;
            const regex = this._reverseRegexes.get(key);
            if (regex) {
                regex.lastIndex = 0;
                result = result.replace(regex, this._reverseDict[key]);
            }
        }
        return result;
    }

    /** 사전 키를 모두 치환 (긴 키 우선) */
    private _replaceAll(text: string, dict: Record<string, string>, sortedKeys: string[]): string {
        let result = text;
        for (const key of sortedKeys) {
            if (result.includes(key)) {
                result = result.split(key).join(dict[key]);
            }
        }
        return result;
    }

    /** Mutation callbacks run at the microtask checkpoint, before the next paint.
     * Never defer this work to a timer/rAF: that can expose the Korean source frame.
     */
    private _observe() {
        this._observer = new MutationObserver(mutations => {
            const roots = new Set<Node>();
            const attributes = new Set<Element>();
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(node => roots.add(node));
                } else if (mutation.type === 'characterData') {
                    roots.add(mutation.target);
                } else if (mutation.type === 'attributes') {
                    attributes.add(mutation.target as Element);
                }
            }
            if (!roots.size && !attributes.size) return;
            this._observer?.disconnect();
            try {
                for (const root of roots) {
                    if (!root.isConnected) continue;
                    // An inserted ancestor already covers this node in the same batch.
                    let parent = root.parentNode;
                    while (parent && !roots.has(parent)) parent = parent.parentNode;
                    if (!parent) this._translateTree(root);
                }
                for (const element of attributes) {
                    if (element.isConnected) this._translateAttributes(element);
                }
            } finally {
                // Our own writes are not observed, so translation cannot loop.
                this._observeBody();
            }
        });
        this._observeBody();
    }
}

export default I18n;
